import React, { useCallback } from 'react'
import { useDraggable, useDroppable } from '@dnd-kit/core'
import { TocRowMenu, tocMenuActions } from './TocRowMenu'
import { TocInlineRename } from './TocInlineRename'
import { TocDragHandle } from './TocDragHandle'
import { decodeHtml } from '@Utils'

export const CourseNavItem = ({
  paddingLeft,
  isLocked,
  itemTitle,
  coursenum,
  href,
  idPath,
  currentPage,
  itemId,
  depth,
  icon,
  tag,
  rawTitle,
  authorEditing,
  editingId,
  onAddItem,
  onStartRename,
  onRename,
  onCancelRename,
  onStartDelete,
  dropIndicator,
  moveBusy,
}) => {
  const linkHref = href || `/reader/web/${coursenum}/`
  const onTitlePage =
    window.location.pathname.split('/').filter(Boolean).length === 3 &&
    window.location.pathname.includes('/reader/web/')
  const isActive =
    currentPage === itemId || (onTitlePage && itemId === undefined)

  const handleKeyDown = useCallback(
    event => {
      // Only handle Enter for actual links (CourseNavItem), not for submenu buttons
      if (event.key === 'Enter' && event.target.tagName === 'A') {
        event.preventDefault()
        event.stopPropagation()
        // Navigate to the href
        window.location.href = linkHref
        return
      }
      // For all other keys (including arrows), do NOT prevent default
      // Let them bubble up to parent for menu navigation
    },
    [linkHref]
  )

  const isEditing = authorEditing && itemId && editingId === itemId

  // drag-and-drop registration; a leaf row can still be a "drop inside"
  // target when it is an empty chapter (childTag null = takes sections)
  const dndData = {
    id: itemId,
    depth,
    tag,
    childTag: null,
    pathIds: (idPath || '').split('/').filter(Boolean),
    title: itemTitle,
  }
  const dndDisabled = !authorEditing || !!moveBusy || !itemId
  const {
    listeners,
    setNodeRef: setDragNodeRef,
    isDragging,
  } = useDraggable({ id: itemId || 'toc-title-page', data: dndData, disabled: dndDisabled })
  const { setNodeRef: setDropNodeRef } = useDroppable({
    id: itemId || 'toc-title-page',
    data: dndData,
    disabled: dndDisabled,
  })
  const setRowRefs = useCallback(
    el => {
      setDragNodeRef(el)
      setDropNodeRef(el)
    },
    [setDragNodeRef, setDropNodeRef]
  )

  const indicator =
    dropIndicator && dropIndicator.id === itemId ? dropIndicator.position : null

  return (
    <li
      className={`CourseNavMenuItem${
        indicator === 'before' ? ' toc-drop-before' : ''
      }${indicator === 'after' ? ' toc-drop-after' : ''}${
        isDragging ? ' toc-row-dragging' : ''
      }`}
      style={{ '--toc-drop-depth': depth }}
      role="none"
    >
      {isEditing ? (
        <div
          className="toc-inline-rename-row"
          style={{
            paddingLeft: `${depth > 1 ? paddingLeft * depth + 36 : 8}px`,
          }}
        >
          <TocInlineRename
            initialValue={rawTitle}
            onSubmit={value => onRename(itemId, value)}
            onCancel={onCancelRename}
          />
        </div>
      ) : (
        <a
          className={`${isActive ? ' active' : ''}${
            indicator === 'inside' ? ' toc-drop-inside' : ''
          }`}
          role="menuitem"
          href={linkHref}
          tabIndex={0}
          onKeyDown={handleKeyDown}
          ref={setRowRefs}
        >
          {/* Add spacing equivalent to chevron icon + its margin */}
          <span
            className="nav-item-text"
            style={{
              marginLeft: `${depth > 1 ? paddingLeft * depth + 36 : '8'}px`,
            }}
          >
            {icon && icon}
            {isLocked && (
              <i
                className="fa-regular fa-lock-alt fa-fw icon_color_orange mr-2"
                aria-hidden="true"
              />
            )}
            {decodeHtml(itemTitle)}
          </span>
        </a>
      )}
      {authorEditing && itemId && !isEditing && (
        <TocDragHandle listeners={listeners} title={decodeHtml(itemTitle)} />
      )}
      {authorEditing && itemId && !isEditing && (
        <TocRowMenu
          title={decodeHtml(itemTitle)}
          actions={tocMenuActions({
            tag,
            childTag: null, // a leaf row has no ToC children, so a child is a section
            depth,
            itemId,
            title: decodeHtml(itemTitle),
            onAddItem,
            onStartRename,
            onStartDelete,
          })}
        />
      )}
    </li>
  )
}
